import axios from 'axios'
import dayjs from "dayjs";
import { useFormik } from 'formik'
import { enqueueSnackbar } from "notistack";
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import * as yup from 'yup'
import { API } from "../../../helpers/constants";
import { handleAxiosErrors } from "../../../helpers/errorHandling";
import { getRouteUrl } from '../../../helpers/routing/getRouteUrl'
import useUserData from "../../../hooks/user/useUserData";
import { REGISTRATIONS } from "../locales/namespaces";
import { ROUTE_PAGE_REGISTRATION_EDIT } from "../routes/routes";

const transformData = (data, registrationId) => {
	return {
		...(data.registrationId && { id: Number(data.registrationId) }),
		...(registrationId && { id: Number(registrationId) }),
		UserId: Number(data.UserId.id),
		ProjectId: Number(data.ProjectId.id),
		WorkDt: dayjs(data.WorkDt).format('YYYY-MM-DD'),
		works: data.projectWorks.map((w) => ({
			...(Number(w.id) && { WorkId: Number(w.id) }),
			UserId: Number(w.UserId?.id),
			WorkTm: Number(w.WorkTm),
			ExtraWorkTm: Number(w.ExtraWorkTm),
		})).filter(f => f.UserId),
	};
};

export const isUUID = (value) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)

const useRegistrationForm = ({ registrationId, registration, projects, users, projectWorks }) => {
	
	const { t } = useTranslation(REGISTRATIONS)
	const { token, roles } = useUserData()
	const [loadingSubmit, setLoadingSubmit] = useState(false)
	
	const onSubmit = (values, updatedRow) => {
		const newLine = values.newLine
		const updatedRowId = updatedRow?.id
		const data = transformData({ ...values, projectWorks: [updatedRow] }, registrationId)
		setLoadingSubmit(true)
		if (data.works?.length > 0) {
			axios({
				method: 'post',
				data: {
					data: { ...data, addLine: Boolean(data.id) }
				},
				headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
				url: newLine ? `${API}/en/fts/project/work/store` : `${API}/en/fts/project/work/update`
			}).then(response => {
				setLoadingSubmit(false)
				console.log('formik update', {
					...values,
					firstRowAdded: true,
					registrationId: response.data.id ? response.data.id : values.registrationId,
					projectWorks: values.projectWorks.map(i => ({
						...i,
						id: isUUID(i.id) && response.data.WorkId ? response.data.WorkId : i.id,
						rowStored: updatedRowId === i.id ? true : i.rowStored
					}))
				})
				console.log('updatedRow', updatedRow)
				console.log('values', values)
				formik.setValues({
					...values,
					firstRowAdded: true,
					registrationId: response.data.id ? response.data.id : values.registrationId,
					projectWorks: values.projectWorks.map(i => ({
						...i,
						id: isUUID(i.id) && response.data.WorkId ? response.data.WorkId : i.id,
						rowStored: updatedRowId === i.id ? true : i.rowStored
					}))
				})
				enqueueSnackbar(t(`Registration ${!registrationId ? 'created' : 'updated'} successfully!`), {
					variant: 'success',
					autoHideDuration: 3000,
				});
				// history.push(getRouteUrl(ROUTE_PAGE_REGISTRATION_EDIT, { registrationId: !registrationId ? response.data.id : registrationId }))
				
			}).catch((error) => {
				setLoadingSubmit(false)
				if (newLine && values.projectWorks?.length === 1) {
					formik.setFieldValue('projectWorks', [])
				}
				let message = error?.response?.data?.message ? t(error?.response?.data?.message) : ''
				const errorMessageData = error?.response?.data?.data
				if (errorMessageData && errorMessageData.length && errorMessageData.length > 0) {
					message += `${message}`
					errorMessageData.forEach(i => {
							message += `${t('registration_error', {
								worker: i.Assignee,
								workDt: dayjs(i.WorkDt).format('DD-MM-YYYY'),
								workHours: Number(i.WorkTm),
								totalHours: Number(i.total)
							})}`
						}
					)
				}
				enqueueSnackbar(handleAxiosErrors(error, message), {
					variant: 'warning',
					autoHideDuration: 9000,
				})
			})
		}
	}
	
	const validationSchema = yup.object().shape({
		ProjectId: yup.mixed().test(
			'acceptedValues', t('Required'), function (value) {
				return value;
			}
		),
		UserId: yup.mixed().test(
			'acceptedValues', t('Required'), function (value) {
				return value;
			}
		),
		WorkDt: yup
		.date()
		.typeError(t('Invalid date'))
		.required('Required')
		.when(['ProjectId.StartDt', 'ProjectId.EndDt'], ([start, end], schema) => {
			if (start && end) {
				const maxDate = dayjs(end).add(1, 'day').format('YYYY-MM-DD')
				return schema
				.min(start, t('dateOutOfRange', {
					start: dayjs(start).format('DD-MM-YYYY'),
					end: dayjs(end).format('DD-MM-YYYY')
				}))
				.max(maxDate, t('dateOutOfRange', {
					start: dayjs(start).format('DD-MM-YYYY'),
					end: dayjs(end).format('DD-MM-YYYY')
				}));
			}
			return schema
		}),
		// projectWorks:  yup.array().min(1, t('Required')).required(t('yup:Required'))
	});
	
	const formik = useFormik({
		initialValues: {
			ProjectId: registration.ProjectId ?
				{ Description: registration.Description, id: registration.ProjectId } :
				'',
			UserId: registration.UserId
				? { Description: registration.Assignee, id: registration.UserId }
				: (roles[0] === '2'
					? { id: users[0]?.id, Description: users[0]?.Description }
					: ''),
			WorkDt: registrationId ? dayjs(registration?.WorkDt) : dayjs(),
			projectWorks: projectWorks && projectWorks.length > 0 ? projectWorks.map(work => ({
				id: work.id,
				UserId: work.UserId && work.Worker ? { id: work.UserId, Description: work.Worker } : '',
				WorkTm: work.WorkTm || '',
				ExtraWorkTm: work.ExtraWorkTm || '',
				rowStored: true
			})) : [],
			restrictions: [],
		},
		validationSchema: validationSchema,
		onSubmit: onSubmit,
	})
	
	return {
		formik,
		loadingSubmit,
		onSubmit
	}
	
}
export default useRegistrationForm